import { formatRefCodeLabel } from "../../tc-service/ref-code-label.utils";

export type TcReasonOption = {
  label: string;
  value: string;
  description: string;
};

const reasonOption = (value: string, description: string): TcReasonOption => ({
  value,
  description,
  label: formatRefCodeLabel(description, value),
});

export const AMENDMENT_REASON_OPTIONS: readonly TcReasonOption[] = [
  reasonOption("R", "Reduction"),
  reasonOption("T", "Typographical errors"),
  reasonOption("C", "Claims category"),
  reasonOption("I", "Incoming transaction certificate"),
  reasonOption("D", "Data correction"),
  reasonOption("E", "Exemption with approval from Textile Exchange"),
];

const normalize = (value: unknown) => String(value ?? "").trim();

// Delay and invalidation reasons are maintained in DTrackIT reference data.
// Preserve the API's rows and order so new or corrected values do not require
// another frontend release. Only normalize the legacy uppercase NA spelling to
// the public-contract value `na`, and discard unusable/duplicate rows.
export const mapApiReasonOptions = (apiRows: any[] = []): TcReasonOption[] => {
  const seenCodes = new Set<string>();

  return apiRows.flatMap((row) => {
    const rawCode = normalize(row?.code_1);
    const value = rawCode.toUpperCase() === "NA" ? "na" : rawCode;
    const description = normalize(row?.description_1);

    if (!value || !description || seenCodes.has(value)) return [];

    seenCodes.add(value);
    return [reasonOption(value, description)];
  });
};

export const mapCanonicalReasonOptions = (
  apiRows: any[] = [],
  requiredOptions: readonly TcReasonOption[]
): TcReasonOption[] => {
  const requiredByCode = new Map(
    requiredOptions.map((option) => [option.value, option.description])
  );
  const apiByCode = new Map(
    apiRows.map((row) => [normalize(row?.code_1), normalize(row?.description_1)])
  );
  const unexpectedCodes = Array.from(apiByCode.keys()).filter(
    (code) => code && !requiredByCode.has(code)
  );
  const missingCodes = Array.from(requiredByCode.keys()).filter(
    (code) => !apiByCode.has(code)
  );
  const descriptionDrift = Array.from(requiredByCode.entries())
    .filter(
      ([code, description]) =>
        apiByCode.has(code) && apiByCode.get(code) !== description
    )
    .map(([code]) => code);

  if (
    unexpectedCodes.length > 0 ||
    missingCodes.length > 0 ||
    descriptionDrift.length > 0
  ) {
    console.warn("TC reason ref-code drift detected; using specification values", {
      unexpectedCodes,
      missingCodes,
      descriptionDrift,
    });
  }

  return requiredOptions.map((option) => ({ ...option }));
};
